import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Search, RefreshCw, Filter, Bookmark, X, Clock, MapPin, LayoutGrid, Map, TrendingUp, Flame, Check, ArrowRight } from "lucide-react";
import { useState, useMemo, lazy, Suspense } from "react";
import { fmtNumber } from "../../lib/safeFormat";
import PropertyCard from "../PropertyCard";
import LockedPropertyCard from "../LockedPropertyCard";
import FilterPanel from "../FilterPanel";
import SavedCombosBar from "../SavedCombosBar";
import UsageMeter from "../UsageMeter";
import StatsBar from "./StatsBar";
import LocationAutocomplete from "../LocationAutocomplete";
import StrategyPicker from "../StrategyPicker";
import { scoreAnalysis, DEFAULT_WEIGHTS } from "../../lib/dealScoring";
import { evaluateStrategyMatch, hasCriteria } from "../../lib/strategyMatch";

const PropertyMap = lazy(() => import("../PropertyMap"));

const SearchTab = ({
  filters,
  setFilters,
  suggestions,
  searchProperties,
  loading,
  smartSort,
  setSmartSort,
  investmentFilters,
  setInvestmentFilters,
  multiSubTypes,
  setMultiSubTypes,
  sortBy,
  setSortBy,
  listingStatuses,
  setListingStatuses,
  subscription,
  properties,
  filteredProperties,
  paginatedProperties,
  totalPages,
  analyses,
  analysisLoadProgress,
  profitableCount,
  avgDealScore,
  avgCashFlow,
  totalAnalyzedFiltered,
  propertiesVisible,
  handlePropertyClick,
  savedPropertyIds,
  handleSaveToggle,
  setUpgradeReason,
  setIsUpgradeOpen,
  currentPage,
  setCurrentPage,
  apiPage,
  setApiPage,
  totalCount,
  mobileFiltersOpen,
  setMobileFiltersOpen,
  onSaveSearch,
  savedSearches,
  onLoadSearch,
  onDeleteSearch,
  hasSearched,
  userMarkets,
  onChangeMarkets,
  strategyMode,
  onStrategyChange,
  isAuthenticated,
  activeStrategyId,
  activeStrategyWeights,
  activeStrategyCriteria,
  onActiveStrategyChange,
  onUpgradeElite,
  onPropertyRefreshed,
}) => {
  const [viewMode, setViewMode] = useState("grid");

  const currentWeights = activeStrategyWeights || DEFAULT_WEIGHTS;
  const isCustomStrategy = activeStrategyId && activeStrategyId !== "balanced";
  const criteriaActive = hasCriteria(activeStrategyCriteria);

  const propertiesWithAnalysis = filteredProperties.map((p) => {
    const baseAnalysis = analyses[p.id] || null;
    if (!baseAnalysis || !isCustomStrategy) {
      return { ...p, analysis: baseAnalysis };
    }
    // Re-score client-side against the active strategy — see
    // /app/frontend/src/lib/dealScoring.js for the mirror of the Python
    // engine. `deal_score_baseline` preserves the Balanced score for
    // reference/tooltip; UI reads `deal_score` for sort + badge.
    const strategyScore = scoreAnalysis(baseAnalysis, currentWeights);
    // Strategy Match (criteria fit) — mirror of strategy_match.py.
    const match = criteriaActive
      ? evaluateStrategyMatch({ ...baseAnalysis, property: baseAnalysis.property || p }, activeStrategyCriteria)
      : null;
    return {
      ...p,
      analysis: {
        ...baseAnalysis,
        deal_score_baseline: baseAnalysis.deal_score,
        deal_score: strategyScore,
        strategy_id: activeStrategyId,
        ...(match ? { strategy_match: match } : {}),
      },
    };
  });

  // Lookup of enriched analyses (strategy re-score + strategy match) so the
  // grid cards get the same enriched payload as bestDeal/map views.
  const enrichedAnalyses = {};
  propertiesWithAnalysis.forEach((p) => { if (p.analysis) enrichedAnalyses[p.id] = p.analysis; });

  // Find the best deal from analyzed properties
  const bestDeal = useMemo(() => {
    if (!propertiesWithAnalysis.length) return null;
    const analyzed = propertiesWithAnalysis.filter(p => p.analysis && p.analysis.deal_score > 0);
    if (!analyzed.length) return null;
    return analyzed.reduce((best, p) => 
      (p.analysis.deal_score > (best.analysis?.deal_score || 0)) ? p : best
    , analyzed[0]);
  }, [propertiesWithAnalysis]);

  // Generate insight bullets for the best deal
  const bestDealInsights = useMemo(() => {
    if (!bestDeal?.analysis) return [];
    const a = bestDeal.analysis;
    const insights = [];

    // Cash flow with annualized "what this means"
    if (a.cash_flow_monthly > 0) {
      const annual = Math.round(a.cash_flow_monthly * 12).toLocaleString();
      insights.push(`+$${Math.round(a.cash_flow_monthly).toLocaleString()}/mo cash flow — that's ~$${annual}/year in profit`);
    }

    // 1% rule or cap rate
    if (a.one_percent_rule) insights.push("Passes the 1% rule — strong rent-to-price ratio");
    if (a.cap_rate >= 7) insights.push(`${fmtNumber(a.cap_rate)}% cap rate — above average for this market`);
    else if (a.cap_rate >= 5) insights.push(`${fmtNumber(a.cap_rate)}% cap rate`);

    // "For You" strategy angle + next step
    if (a.cash_flow_monthly > 200 && a.cap_rate >= 6) {
      insights.push("Strong cash flow for buy-and-hold strategy — could be rented immediately");
    } else if (a.cash_flow_monthly > 0) {
      insights.push("Positive cash flow — meets typical buy-and-hold investor criteria");
    }

    return insights.slice(0, 3);
  }, [bestDeal]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-8">
      {/* Mobile quick search bar — always visible on mobile */}
      <div className="lg:hidden" data-testid="mobile-search-bar">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <LocationAutocomplete
              value={filters.location}
              onChange={(val) => setFilters((f) => ({ ...f, location: val }))}
              onSelect={(display) => {
                setFilters((f) => ({ ...f, location: display }));
                setTimeout(() => {
                  searchProperties();
                  setMobileFiltersOpen(false);
                }, 150);
              }}
              placeholder="City, State, or Zip"
              className=""
            />
          </div>
          <Button
            size="sm"
            className="flex-shrink-0 h-9 px-3"
            onClick={() => {
              searchProperties();
              setMobileFiltersOpen(false);
            }}
            disabled={loading}
            data-testid="mobile-search-btn"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      <div className="lg:hidden">
        <Button
          variant="outline"
          size="sm"
          className="w-full flex items-center justify-center gap-2 h-8 text-xs"
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          data-testid="mobile-filters-toggle"
        >
          <Filter className="w-3.5 h-3.5" />
          {mobileFiltersOpen ? "Hide Filters" : "Filters"}
          {filters.location && (
            <Badge className="bg-emerald-500 text-white border-0 text-[10px] ml-1">
              {filters.location}
            </Badge>
          )}
        </Button>
      </div>
      <aside
        className={`lg:col-span-3 space-y-4 lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto lg:sticky lg:top-20 lg:pr-1 ${
          mobileFiltersOpen ? "block" : "hidden lg:block"
        }`}
      >
        {/* User Markets quick-switch */}
        {userMarkets && userMarkets.length > 0 && (
          <Card className="bg-card border-border p-3" data-testid="user-markets-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Your Markets</span>
              </div>
              <button
                onClick={onChangeMarkets}
                className="text-[10px] text-emerald-500 hover:text-emerald-400 transition-colors"
                data-testid="change-markets-btn"
              >
                Change
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {userMarkets.map((market) => (
                <button
                  key={market}
                  onClick={() => {
                    setFilters((f) => ({ ...f, location: market }));
                    setTimeout(() => searchProperties(), 100);
                  }}
                  className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                    filters.location === market
                      ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-500"
                      : "bg-secondary/30 border-border text-muted-foreground hover:text-foreground hover:border-emerald-500/30"
                  }`}
                  data-testid={`market-switch-${market.replace(/[,\s]+/g, "-")}`}
                >
                  {market}
                </button>
              ))}
            </div>
          </Card>
        )}
        <SavedCombosBar
          subscription={subscription}
          buildCurrentSnapshot={() => ({
            filters, listingStatuses, smartSort, sortBy,
            investmentFilters, multiSubTypes,
          })}
          onLoad={(snap) => {
            if (snap?.filters) setFilters(snap.filters);
            if (snap?.listingStatuses) setListingStatuses(snap.listingStatuses);
            if (typeof snap?.smartSort === "boolean") setSmartSort(snap.smartSort);
            if (snap?.sortBy) setSortBy(snap.sortBy);
            if (snap?.investmentFilters) setInvestmentFilters(snap.investmentFilters);
            if (snap?.multiSubTypes) setMultiSubTypes(snap.multiSubTypes);
            // Kick off search after state settles (microtask so setters
            // flush before the fetch reads them).
            queueMicrotask(() => searchProperties());
          }}
        />
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          onSearch={() => {
            searchProperties();
            setMobileFiltersOpen(false);
          }}
          loading={loading}
          smartSort={smartSort}
          setSmartSort={setSmartSort}
          investmentFilters={investmentFilters}
          setInvestmentFilters={setInvestmentFilters}
          multiSubTypes={multiSubTypes}
          setMultiSubTypes={setMultiSubTypes}
          sortBy={sortBy}
          setSortBy={setSortBy}
          listingStatuses={listingStatuses}
          setListingStatuses={setListingStatuses}
          onSaveSearch={onSaveSearch}
        />
        {/* Saved Searches */}
        {savedSearches && savedSearches.length > 0 && (
          <Card className="bg-card border-border p-4" data-testid="saved-searches-panel">
            <div className="flex items-center gap-2 mb-3">
              <Bookmark className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Saved Searches</span>
            </div>
            <div className="space-y-2">
              {savedSearches.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors group" data-testid={`saved-search-${s.id}`}>
                  <button onClick={() => onLoadSearch(s)} className="flex-1 text-left">
                    <p className="text-sm font-medium text-foreground truncate">{s.name}</p>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {s.filters?.location || "All"}
                    </p>
                  </button>
                  <button onClick={() => onDeleteSearch(s.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-muted-foreground hover:text-red-500" data-testid={`delete-search-${s.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        )}
        <UsageMeter subscription={subscription} />
      </aside>
      <section className="lg:col-span-9">
        {properties.length > 0 && Object.keys(analyses).length > 0 && (
          <StatsBar
            profitableCount={profitableCount}
            totalAnalyzed={totalAnalyzedFiltered || Object.keys(analyses).length}
            avgDealScore={avgDealScore}
            avgCashFlow={avgCashFlow}
            analysisLoadProgress={analysisLoadProgress}
            properties={properties}
          />
        )}
        {properties.length > 0 && analysisLoadProgress.loading && (
          <div className="mb-4">
            <div className="h-1 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all duration-500 rounded-full"
                style={{
                  width: `${
                    (analysisLoadProgress.loaded /
                      properties.length) *
                    100
                  }%`,
                }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Analyzing deals… <span className="font-medium text-foreground">{analysisLoadProgress.loaded} of{" "}
              {properties.length} complete</span>
              {profitableCount > 0 && (
                <span className="text-emerald-500 ml-1">— {profitableCount} profitable so far</span>
              )}
            </p>
          </div>
        )}
        {loading && properties.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
            <p className="text-sm text-muted-foreground">Searching properties...</p>
            <p className="text-xs text-muted-foreground/60">First searches may take a moment while we fetch fresh market data</p>
          </div>
        ) : !hasSearched && properties.length === 0 ? (
          <Card className="bg-card border-border p-12 text-center" data-testid="search-prompt">
            <Search className="w-14 h-14 mx-auto text-emerald-500/40 mb-4" />
            <h3 className="text-xl font-bold mb-2">Search to Get Started</h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
              Enter a city, state, or zip code and hit search to discover investment properties with full cash flow analysis.
            </p>
            {subscription && !subscription.is_paid && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/60 border border-border text-xs text-muted-foreground">
                <span>Free plan: {subscription.analysis_limit} analyses/week</span>
                <span className="text-emerald-500 font-semibold cursor-pointer" onClick={() => { setUpgradeReason("analysis"); setIsUpgradeOpen(true); }}>
                  Upgrade for unlimited
                </span>
              </div>
            )}
          </Card>
        ) : filteredProperties.length === 0 ? (
          <Card className="bg-card border-border p-12 text-center">
            <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            {analysisLoadProgress.loading &&
            (investmentFilters.onlyPositiveCashFlow ||
              investmentFilters.only1PercentRule ||
              investmentFilters.only50PercentRule) ? (
              <>
                <h3 className="text-lg font-semibold mb-2">
                  Analyzing properties...
                </h3>
                <p className="text-muted-foreground">
                  {analysisLoadProgress.loaded} of{" "}
                  {analysisLoadProgress.total} analyzed
                </p>
              </>
            ) : properties.length > 0 ? (
              <>
                <h3 className="text-lg font-semibold mb-2">
                  0 of {analysisLoadProgress.loaded} analyzed properties match
                  your filters
                </h3>
                <p className="text-muted-foreground">
                  Try turning off some investment filters or broadening your
                  search
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-2">
                  No Properties Found
                </h3>
                <p className="text-muted-foreground mb-3">
                  Try adjusting your search filters
                </p>
                <p className="text-xs text-muted-foreground/80 max-w-md mx-auto leading-relaxed">
                  Tip: broad city searches currently pull from public
                  property records (off-market). If you&apos;re set to
                  <span className="font-semibold text-foreground"> For Sale only</span>,
                  toggle on <span className="font-semibold text-foreground">Recently Sold</span>
                  or <span className="font-semibold text-foreground">Off Market</span> to
                  see records — or search a full street address for the
                  most complete data.
                </p>
              </>
            )}
          </Card>
        ) : (
          <>
            {/* Best Deal Highlight — shows when analysis is complete */}
            {bestDeal?.analysis && strategyMode === 'rental' && (
              <div
                className="relative rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/[0.04] to-transparent p-4 mb-4 cursor-pointer hover:border-emerald-500/50 transition-colors group"
                onClick={() => handlePropertyClick(bestDeal)}
                data-testid="best-deal-highlight"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Flame className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-xs font-bold text-emerald-500 uppercase tracking-wide">Best Deal Found</p>
                      <Badge className="bg-emerald-500 text-white border-0 text-[10px]">{bestDeal.analysis.deal_score}/100</Badge>
                      {bestDeal.analysis.deal_score >= 75 ? (
                        <span className="text-[10px] text-emerald-500/70 font-medium">Top 5% of deals in this market</span>
                      ) : bestDeal.analysis.deal_score >= 50 ? (
                        <span className="text-[10px] text-muted-foreground/70 font-medium">Better than most deals in this market</span>
                      ) : null}
                    </div>
                    <p className="text-sm font-semibold text-foreground truncate">{bestDeal.address} — ${(bestDeal.price || 0).toLocaleString()}</p>
                    {bestDealInsights.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {bestDealInsights.map((insight, i) => (
                          <p key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                            {insight}
                          </p>
                        ))}
                        <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1.5 mt-1">
                          <span className="w-3" />Rent estimate based on listing data + market comps
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                      <span className="text-xs font-medium text-emerald-500 bg-emerald-500/10 px-2.5 py-1 rounded-md group-hover:bg-emerald-500/20 transition-colors">
                        View Full Breakdown
                      </span>
                      <span className="text-[10px] text-muted-foreground/50">Properties with this cash flow are rare in {filters.location || 'this market'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Anonymous usage progress */}
            {!isAuthenticated && hasSearched && (
              <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20 mb-4" data-testid="anon-usage-bar">
                <div>
                  <p className="text-xs text-foreground font-medium">You've used 1 of 3 free searches</p>
                  <p className="text-[10px] text-muted-foreground">Unlock unlimited deals and full analysis breakdowns</p>
                </div>
                <Button
                  size="sm"
                  className="text-xs h-7 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => window.location.href = '/auth?mode=signup'}
                  data-testid="anon-signup-nudge"
                >
                  Unlock Unlimited
                </Button>
              </div>
            )}

            {/* Strategy Toggle */}
            <div className="flex items-center gap-3 mb-4" data-testid="strategy-toggle">
              <div className="flex items-center gap-0.5 bg-secondary/50 p-0.5 rounded-lg border border-border">
                <button
                  onClick={() => onStrategyChange('rental')}
                  className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-colors ${strategyMode === 'rental' ? 'bg-emerald-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="strategy-rental-btn"
                >
                  Rental
                </button>
                <button
                  onClick={() => onStrategyChange('flip')}
                  className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-colors ${strategyMode === 'flip' ? 'bg-orange-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="strategy-flip-btn"
                >
                  Flip
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground hidden sm:block">
                {strategyMode === 'rental' ? 'Showing cash flow, cap rate, rent metrics' : 'Showing ARV, rehab, MAO, flip profit'}
              </p>
            </div>

            <div className="flex items-center justify-between mb-4">
              {filteredProperties.length !== properties.length ? (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Showing{" "}
                    <span className="font-bold text-foreground">
                      {filteredProperties.length}
                    </span>{" "}
                    of {properties.length} properties matching filters
                    {totalPages > 1 && ` (page ${currentPage}/${totalPages})`}
                  </p>
                  {analysisLoadProgress.loading && filteredProperties.length < 12 && (
                    <p className="text-xs text-emerald-500 flex items-center gap-1 mt-0.5">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      Still analyzing — more matches may appear ({analysisLoadProgress.loaded}/{analysisLoadProgress.total} done)
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Showing {paginatedProperties.length} of {filteredProperties.length} properties
                  {totalPages > 1 && ` (page ${currentPage}/${totalPages})`}
                </p>
              )}
              <div className="flex items-center gap-2">
                <StrategyPicker
                  value={activeStrategyId || "balanced"}
                  onChange={onActiveStrategyChange}
                  isElite={!!subscription?.is_elite}
                  onUpgrade={onUpgradeElite}
                />
                <div className="flex items-center gap-1 bg-secondary/40 p-0.5 rounded-lg border border-border" data-testid="view-mode-toggle">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === "grid" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="view-grid-btn"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" /> Grid
                  </button>
                  <button
                    onClick={() => setViewMode("map")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === "map" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="view-map-btn"
                  >
                    <Map className="w-3.5 h-3.5" /> Map
                  </button>
                </div>
              </div>
            </div>

            {viewMode === "map" ? (
              <Suspense fallback={<div className="h-[500px] rounded-lg bg-card border border-border flex items-center justify-center"><RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
                <PropertyMap
                  properties={propertiesWithAnalysis}
                  onPropertyClick={handlePropertyClick}
                  searchLocation={filters.location}
                />
              </Suspense>
            ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5"
              data-testid="property-grid"
            >
              {paginatedProperties.map((property, index) => {
                const isLocked =
                  propertiesVisible > 0 && index >= propertiesVisible;
                return (
                  <div
                    key={property.id}
                    className={`animate-slide-up stagger-${
                      (index % 6) + 1
                    }`}
                  >
                    {isLocked ? (
                      <LockedPropertyCard
                        property={property}
                        onUpgrade={() => {
                          setUpgradeReason("properties");
                          setIsUpgradeOpen(true);
                        }}
                      />
                    ) : (
                      <PropertyCard
                        property={property}
                        analysis={enrichedAnalyses[property.id] || analyses[property.id]}
                        onClick={() => handlePropertyClick(property)}
                        isSaved={savedPropertyIds.has(property.id)}
                        onSaveToggle={handleSaveToggle}
                        showFlipData={investmentFilters?.showFlipData}
                        showAirbnbData={investmentFilters?.showAirbnbData}
                        strategyMode={strategyMode}
                        subscription={subscription}
                        onRefreshed={onPropertyRefreshed}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </>
        )}
        {/* Pagination: local pages + API pages */}
        {filteredProperties.length > 0 && (totalPages > 1 || totalCount > properties.length) && (
          <div
            className="flex flex-col items-center gap-3 mt-8"
            data-testid="pagination"
          >
            {/* Local page navigation within current batch */}
            {totalPages > 1 && (
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }); }}
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page{" "}
                  <span className="font-mono font-bold text-foreground">{currentPage}</span>
                  {" "}of{" "}
                  <span className="font-mono font-bold text-foreground">{totalPages}</span>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }); }}
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            )}

            {/* Load more from Zillow API */}
            {totalCount > properties.length && (
              <Button
                variant="default"
                onClick={() => {
                  const nextApiPage = apiPage + 1;
                  setApiPage(nextApiPage);
                  setCurrentPage(1);
                  searchProperties(nextApiPage);
                  window.scrollTo({ top: 0 });
                }}
                disabled={loading}
                className="w-full max-w-md"
                data-testid="load-more-btn"
              >
                {loading ? "Loading..." : `Load Next ${Math.min(41, totalCount - properties.length)} Properties (${properties.length} of ${totalCount.toLocaleString()} loaded)`}
              </Button>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default SearchTab;
